import React, { useEffect, useState } from "react";
import { Card, CardContent, Typography, CircularProgress, Box, Chip, Alert, Avatar, Stack, CardHeader, Dialog, DialogTitle, DialogContent, DialogActions, Button, Snackbar, Grid } from "@mui/material";
import ApartmentIcon from '@mui/icons-material/Apartment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupsIcon from '@mui/icons-material/Groups';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import MDButton from "components/MDButton";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Tooltip from "@mui/material/Tooltip";
import confetti from "canvas-confetti";
import { API_ENDPOINTS, getUploadUrl } from "config/api";
import { decryptText } from "config/encryption";

const statusColor = (status) => {
  if (status === "Coming Soon") return "warning";
  if (status === "Completed") return "success";
  return "info";
};

const ANIMATION_DURATION = 80; // seconds, slower marquee movement

const EventList = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [subscribeDialogOpen, setSubscribeDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [subscribing, setSubscribing] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState(null);
  const [selectedEventName, setSelectedEventName] = useState("");

  useEffect(() => {
    setLoading(true);
    let token = "";
    let studentId = "";
    try {
      const encrypted = localStorage.getItem("UserLogDetails");
      if (encrypted) {
        const decryptedUser = decryptText(encrypted);
        const userObj = JSON.parse(decryptedUser);
        token = userObj.Token;
        studentId = userObj.StudentId;
      }
    } catch (err) {
      setError("Failed to get user token");
      setLoading(false);
      return;
    }
    fetch(API_ENDPOINTS.PROFILE.FETCH_CHAPTER_EVENTS_WITH_DETAILS(studentId), {
      headers: {
        Authorization: `Bearer ${token}`,
        
      },
    })
      .then(res => {
        if (!res.ok) throw new Error("Failed to fetch events");
        return res.json();
      })
      .then(data => {
        setEvents(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const handleOpenMembersDialog = async (event) => {
    setMembersDialogOpen(true);
    setMembers([]);
    setMembersLoading(true);
    setMembersError(null);
    setSelectedEventName(event.eventName);
    try {
      let token = "";
      try {
        const encrypted = localStorage.getItem("UserLogDetails");
        if (encrypted) {
          const decryptedUser = decryptText(encrypted);
          const userObj = JSON.parse(decryptedUser);
          token = userObj.Token;
        }
      } catch (err) {
        setMembersError("Failed to get user token");
        setMembersLoading(false);
        return;
      }
      const res = await fetch(API_ENDPOINTS.PROFILE.FETCH_EVENTS_MEMBERS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ eventId: event.eventId }),
      });
      if (!res.ok) throw new Error("Failed to fetch members");
      const data = await res.json();
      setMembers(data);
    } catch (err) {
      setMembersError(err.message);
    }
    setMembersLoading(false);
  };

  if (loading) return <Box display="flex" justifyContent="center" my={4}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  // Duplicate events for seamless loop
  const marqueeEvents = [...events, ...events];
  const cardWidth = 340 + 24; // card width + gap
  const totalWidth = marqueeEvents.length * cardWidth;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        py: 2,
        px: 1,
      }}
    >
      {/* Keyframes style for animation */}
      <style>{`
        @keyframes marqueeScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-${events.length * cardWidth}px); }
        }
      `}</style>
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          width: totalWidth,
          animation: `marqueeScroll ${ANIMATION_DURATION}s linear infinite`,
          '&:hover': { animationPlayState: 'paused' },
        }}
      >
        {marqueeEvents.map((event, idx) => (
          <Card
            key={idx + '-' + event.eventId}
            onClick={() => {
              if (event.isJoined === 1) {
                handleOpenMembersDialog(event);
              } else {
                setSnackbar({
                  open: true,
                  message: "You are not joined in this event. Please join.",
                  severity: "warning"
                });
              }
            }}
            sx={{
              minWidth: 350,
              maxWidth: 370,
              flex: '0 0 auto',
              borderRadius: 3,
              boxShadow: 2,
              background: '#fff',
              border: '1px solid #f0f0f0',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'scale(1.03)',
                boxShadow: 6,
                cursor: 'pointer'
              },
              p: 0,
              overflow: 'visible',
            }}
          >
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: '#1976d2', width: 48, height: 48, fontWeight: 700, fontSize: 22 }}>
                  {event.chapterShortName || event.chapterName?.[0] || '?'}
                </Avatar>
              }
              action={
                <>
                  <style>
                    {`
                      @keyframes blink-subscribe {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.3; }
                      }
                    `}
                  </style>
                  {event.isJoined === 0 && (
                    <Tooltip title="Subscribe to this event" arrow>
                      <NotificationsNoneIcon
                        sx={{
                          color: "#fff",
                          cursor: "pointer",
                          fontSize: 28,
                          animation: "blink-subscribe 2s infinite",
                          ml: 1
                        }}
                        onClick={async e => {
                          e.stopPropagation();
                          if (event.eventStatus === "Completed") {
                            setSnackbar({ open: true, message: "This event is completed. Showing event details.", severity: "info" });
                            await handleOpenMembersDialog(event);
                            return;
                          }
                          setSelectedEvent(event);
                          setSubscribeDialogOpen(true);
                        }}
                      />
                    </Tooltip>
                  )}
                </>
              }
              title={
                <Box sx={{ position: "relative", minHeight: 36 }}>
                  <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ fontSize: 18, lineHeight: 1.2, wordBreak: 'break-word', maxWidth: 200 }}
                    >
                      {event.eventName}
                    </Typography>
                    <Chip
                      label={event.eventStatus}
                      color={statusColor(event.eventStatus)}
                      size="small"
                      sx={{ fontWeight: 600, flexShrink: 0, maxWidth: 100, ml: 1 }}
                    />
                  </Box>
                </Box>
              }
              subheader={
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {new Date(event.eventDate).toLocaleDateString()} | {event.venue}
                </Typography>
              }
              sx={{
                background: 'linear-gradient(90deg, #1976d2 0%, #43a047 100%)',
                color: '#fff',
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                pb: 1,
                pt: 2,
                px: 2,
                '& .MuiTypography-root': { color: '#fff' },
                '& .MuiAvatar-root': { boxShadow: 2, border: '2px solid #fff' },
              }}
            />
            <Box sx={{ position: "relative" }}>
              {/* Watermark for joined */}
              {event.isJoined === 1 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%) rotate(-20deg)",
                    color: "rgba(56, 183, 74, 0.18)", // green, low transparency
                    fontWeight: 900,
                    fontSize: 48,
                    letterSpacing: 8,
                    pointerEvents: "none",
                    userSelect: "none",
                    zIndex: 2,
                    whiteSpace: "nowrap",
                  }}
                >
                  JOINED
                </Box>
              )}
              <CardContent sx={{ pt: 2, pb: 2, px: 2, position: "relative", zIndex: 3 }}>
                <Typography variant="body2" color="text.primary" mb={1.5} sx={{ minHeight: 40 }}>
                  {event.description}
                </Typography>
                <Stack spacing={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ApartmentIcon sx={{ color: '#1976d2', bgcolor: '#e3f2fd', borderRadius: '50%', p: 0.5 }} />
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {event.chapterName}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationOnIcon sx={{ color: '#43a047', bgcolor: '#e8f5e9', borderRadius: '50%', p: 0.5 }} />
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {event.location}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <GroupsIcon sx={{ color: '#f9a825', bgcolor: '#fffde7', borderRadius: '50%', p: 0.5 }} />
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {event.eventmemberCount} Members Joined
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Box>
          </Card>
        ))}
      </Box>
      <Dialog open={subscribeDialogOpen} onClose={() => setSubscribeDialogOpen(false)}>
        <DialogTitle>Join Event</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ fontSize: 12 }}>
            Are you sure you want to join this event
            {selectedEvent && (
              <>
                : <b>{selectedEvent.eventName}</b>?
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => setSubscribeDialogOpen(false)}
            color="secondary"
            variant="outlined"
            sx={{ minWidth: 90, fontWeight: 600 }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={async () => {
              setSubscribing(true);
              try {
                const user = JSON.parse(decryptText(localStorage.getItem("UserLogDetails")));
                const res = await fetch(API_ENDPOINTS.PROFILE.MERGE_EVENTS_MEMBER, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user.Token}`,
                  },
                  body: JSON.stringify({
                    eventsMemberId: 0,
                    eventId: selectedEvent.eventId,
                    studentId: user.StudentId,
                    role: "Member",
                    isCoordinator: false,
                    isActive: true,
                    joinedDate: new Date().toISOString(),
                  }),
                });
                const data = await res.json();
                setSnackbar({ open: true, message: data.Message || "Subscribed", severity: "success" });
                confetti({
                  particleCount: 100,
                  spread: 70,
                  origin: { y: 0.6 },
                });
                // Refresh events after joining
                setLoading(true);
                fetch(`${API_URL}?StudentId=${user.StudentId}`, {
                  headers: { Authorization: `Bearer ${user.Token}` },
                })
                  .then(res => {
                    if (!res.ok) throw new Error("Failed to fetch events");
                    return res.json();
                  })
                  .then(data => {
                    setEvents(data);
                    setLoading(false);
                  })
                  .catch(err => {
                    setError(err.message);
                    setLoading(false);
                  });

              } catch (err) {
                setSnackbar({ open: true, message: "Failed to subscribe", severity: "error" });
                setLoading(false);
              }
              setSubscribing(false);
              setSubscribeDialogOpen(false);
            }}
            color="info"
            variant="gradient"
            sx={{ minWidth: 110, fontWeight: 600 }}
            disabled={subscribing}
          >
            Join
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={membersDialogOpen}
        onClose={() => setMembersDialogOpen(false)}
        fullScreen
      >
        <DialogTitle>
          Members Joined: {selectedEventName}
          <IconButton
            aria-label="close"
            onClick={() => setMembersDialogOpen(false)}
            sx={{ position: 'absolute', right: 16, top: 16, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {membersLoading && <Box display="flex" justifyContent="center" my={4}><CircularProgress /></Box>}
          {membersError && <Alert severity="error">{membersError}</Alert>}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {members.map((member) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={member.eventsMemberId}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 2,
                    bgcolor: "#fafafa",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minHeight: 220,
                  }}
                >
                  <Avatar
                    src={member.img ? getUploadUrl(member.img) : undefined}
                    alt={member.fullName}
                    sx={{ width: 80, height: 80, mb: 2, fontSize: 32 }}
                  >
                    {member.fullName?.[0] || "?"}
                  </Avatar>
                  <Typography fontWeight={700} fontSize={18} gutterBottom>
                    {member.fullName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Role: {member.role}
                  </Typography>
                  {member.customBatch && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, fontStyle: 'italic', mb: 0.5 }}>
                      Batch: {member.customBatch}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Joined: {member.joinedDate && new Date(member.joinedDate).toLocaleDateString()}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          {!membersLoading && members.length === 0 && (
            <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>
              No members have joined this event yet.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end", pb: 3, pr: 3 }}>
          <MDButton
            onClick={() => setMembersDialogOpen(false)}
            color="info"
            variant="contained"
          >
            Close
          </MDButton>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EventList;
